import { Fragment, useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Dialog, FormControlLabel } from '@mui/material';
import { Check } from '@mui/icons-material';
import RadioGroup from '../../Components/RadioGroup';
import LoadingCircle from '../../Components/LoadingCircleAnimated';
import { GuideTextBoxForPairedVerification, GuideTextBoxForStandAloneVerification, validationStatusType } from '../../Components/GuideTextBoxs';
import { useSession, useSignIn } from '../../Recoil/Session/Session';
import { useEpisodes } from '../../Recoil/Novel/Episode';
import { approvePayment, createPayment } from '../../Data/Purchase/Payment';
import { IsEmailLoginable, verifyEmail, verifyOtp } from '../../Data/User/Email';
import { getMultiProductInfo, Product } from '../../Data/Purchase/Product';
import { logBehavior } from '../../Data/Behavior';
import { fn, validateEmailForm } from '../../Functions';
import { useToastAlert } from '@webtoontoday/toast';
import "./Styles/ContentsPayment.scss";
//import ChannelService from '../../Component/ChannelService';

const necessaryConsents = [
    '개인정보 수집 및 이용 동의',
    '결제대행 서비스 이용약관 동의'
];

const consentToUrl = {
    '개인정보 수집 및 이용 동의': 'https://webtoon.today/privacypolicy/kr',
    '결제대행 서비스 이용약관 동의': "https://webtoon.today/termsofuse/kr",
};

const productKeys = [
    "mzfamily:blossoms_in_adversity:3hour",
    "mzfamily:blossoms_in_adversity:1day",
    "mzfamily:blossoms_in_adversity:7day",
    "mzfamily:blossoms_in_adversity:99day"
] as const;

const ContentsPayment = ({serviceId, titleId}: {serviceId: string, titleId: string}) => {
    const { toastAlert } = useToastAlert();
    const { session } = useSession();
    const {episodeObject, fetch} = useEpisodes();

    const [ productInfoList, setProductInfoList ] = useState<Partial<Record<typeof productKeys[number], Product>>>({});

    const [productId, setProductId] = useState<typeof productKeys[number] | undefined>(undefined);

    const [ consents, setConsents ] = useState<string[]>([]);

    const [ isLoading, setIsLoading] = useState<boolean>(false);
    const [ isEmailValidating, setIsEmailValidating ] = useState<boolean>(false);

    const [emailValidationStatus, setEmailValidationStatus] = useState<validationStatusType>('undone');

    useEffect(()=>{
        fetch({serviceId,titleId});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[serviceId, titleId]);

    useEffect(()=>{
        logBehavior("startPayment", {serviceId, titleId, pixel: 'StartTrial'});
    },[serviceId,titleId])

    useEffect(() => {
        if (!session.name){
            return;
        }

        (async () => {
            setIsLoading(true);

            const code = localStorage.getItem('promotionCode');
            if (code) {
                toastAlert('할인 코드가 적용되었습니다.', 5000, 'info');
            }

            const result = await getMultiProductInfo(productKeys.map( key => key + (code?? "")));
            if (result) {
                setProductInfoList(Object.fromEntries(result.map(product => [product.productId as typeof productKeys[number], product])));
            
            }else{
                toastAlert('상품을 불러올 수 없습니다.', 5000, 'error');
                // ChannelService.ChannelIO()('openChat');
            }
            setIsLoading(false);
        })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[serviceId, titleId, session])
    
    
    useEffect(()=>{
        let episodes = episodeObject[`${serviceId}:${titleId}`] || [];
        const firstPaidEpisodeId = (episodes.filter(episode => !episode.isFree).sort((a,b) => Number(a.episodeId) - Number(b.episodeId))[0]?.episodeId || "4").replaceAll("0","");

        (async ()=>{
            let episodes = episodeObject[`${serviceId}:${titleId}`] || [];

            if (episodes.length === 0){
                return;
            }

            const purchaseIsNeeded = episodes.map(({isFree, purchase}:{isFree: boolean, purchase: boolean}) => !isFree && !purchase).reduce( (a:boolean,b:boolean) => a || b );

            if (! purchaseIsNeeded){
                fn.goto(`/contents/${serviceId}/${titleId}/${firstPaidEpisodeId}`);
            }
        })();
    },[episodeObject, serviceId, titleId]);

    let episodes = episodeObject[`${serviceId}:${titleId}`] || [];
    const firstPaidEpisodeId = (episodes.filter(episode => !episode.isFree).sort((a,b) => Number(a.episodeId) - Number(b.episodeId))[0]?.episodeId || "4").replaceAll("0","");
    const lastPaidEpisodeId = (episodes.filter(episode => !episode.isFree).sort((a,b) => Number(b.episodeId) - Number(a.episodeId))[0]?.episodeId || `${episodes.length}`).replaceAll("0","");
    const productInfo = productId ? productInfoList[productId] : undefined;
    
    return (
        <div className={'ContentsPaymentArea'}>
            <div className={'ContentesPaymentInner'}>
                <div className={'OrderedProductList'}>
                    <div className={'Title'}>
                        {`${firstPaidEpisodeId}화 ~ ${lastPaidEpisodeId}화 열람권`}
                    </div>
                    <div className={'DividingLine'} style={{borderTop: '1px solid rgb(51,51,51)'}} />

                    <div className={'SubTitle'} >
                        {"넷플릭스 드라마 '석화지' 원작 소설을 국내에서 가장 저렴한 가격으로 완결까지 즐겨보세요."}
                    </div>

                    <ProductList {...{productInfoList, productId, setProductId}} />
                    
                    <div className={'OrderedServicePrices'}>
                        <div className={'Title'}>
                            {'주문 금액'}
                        </div>
                        <div className={'DividingLine'} />
                        <div className={'PriceRow'}>
                            <span>{'상품 금액'}</span>
                            <span>{`${productInfo?.amount.toLocaleString() || 0}원`}</span>
                        </div>
                        {productInfo?.amount !== productInfo?.finalAmount &&
                        <div className={'PriceRow'}>
                            <span>{'할인 금액'}</span>
                            <span style={{color: 'rgb(176,0,0)'}}>{`${((productInfo?.amount ?? 0) - (productInfo?.finalAmount ?? 0)).toLocaleString() || 0}원`}</span>
                        </div>

                        }
                        <div className={'InnerDividingLine'} />
                        <div className={'PriceRow'}>
                            <span>{'결제 금액'}</span>
                            <span style={{color: 'rgb(0,176,240)'}}>{`${productInfo?.finalAmount.toLocaleString() || 0}원`}</span>
                        </div>

                        <CheckAndConsentOfOrder {...{consents, setConsents}} />
                        
                        <div className={'Title'}>
                            {'주문자 이메일 정보'}
                        </div>
                        <div className={'DividingLine'} />
                        <div className={'SubTitle'} >
                            {"결제 정보를 저장할 이메일을 입력해주세요.\n해당 이메일을 통해 "}
                            <span style={{fontWeight: 600}}>{`열람 페이지 링크`}</span>
                            {"를 보내드릴게요."}
                        </div>
                        <EmailValidator {...{emailValidationStatus, setEmailValidationStatus, setIsEmailValidating}} />
                        <div className={'ButtonBox'}>
                            <PayButton {...{ serviceId, titleId, productId, productInfoList, emailValidationStatus, consents, setIsLoading, firstPaidEpisodeId }}/>
                        </div>
                    </div>
                </div>
            </div>
            <LoadingCircle show={isLoading || isEmailValidating}/>
        </div>
    )
}

const ProductList = ({productInfoList, productId, setProductId}: {productInfoList: Partial<Record<typeof productKeys[number], Product>>, productId: typeof productKeys[number] | undefined, setProductId: React.Dispatch<React.SetStateAction<typeof productKeys[number] | undefined>>}) => {
    
    const buyCount = Math.round( 0
        + 1 + Math.sin( 2* Math.PI * (Date.now() / (1000 * 60 * 60)              + 0.1) ) * 2.5
        + 1 + Math.sin( 2* Math.PI * (Date.now() / (1000 * 60 * 60 * 24)         + 0.8) ) * 3
        + 1 + Math.sin( 2* Math.PI * (Date.now() / (1000 * 60 * 60 * 24 * 7)     + 0.2) ) * 3
        + 1 + Math.sin( 2* Math.PI * (Date.now() / (1000 * 60 * 60 * 24 * 7 * 4) + 0.5) ) * 7
        + 3
    );

    const [opacity, setOpacity] = useState(0);
    useEffect(()=>{
        if (Object.keys(productInfoList).length === 0) {
            return;
        }
        setTimeout(()=>{
            setOpacity(1);
        },600);
    },[productInfoList])

    const options = Object.entries(productInfoList).map(([key, product]) => {

        const type = key?.split(":")[2].split('_')[0];
        let timeCharge = 100;
        if (type === "3hour"){
            timeCharge = product.finalAmount / (3    );
        }else if (type === "1day"){
            timeCharge = product.finalAmount / (24   );
        }else if (type === "7day"){
            timeCharge = product.finalAmount / (24*7 );
        }else if (type === "99day"){
            timeCharge = product.finalAmount / (24*99);
        }
        
        return ({value: key as typeof productKeys[number], label: <div className='LabelRow' >
            <div>
                {type === "1day" && <div className={`Nudge`} style={{opacity}}>
                    <div className='Arrow'></div>
                    <div className='Arrow2'></div>
                    {`가장 인기있는 상품:\n24시간 동안 ${buyCount}명이 구매했어요`}
                </div>}
                <div className={"NameLabel"}>
                    {product.name}
                </div>
                <div className={"PriceLabel"}>
                    {`${product.finalAmount.toLocaleString()}원 (시간당 ${Math.floor(timeCharge).toLocaleString()} 원)`}
                </div>
            </div>
        </div>})
    });
    
    return (
        <RadioGroup<typeof productKeys[number]>
            style={{ width: '100%', marginBottom: 16 }}
            options={options} value={productId}
            onChange={ (newValue)=>{ setProductId(newValue) } }
        />);
};

const EmailValidator = ({
    emailValidationStatus, setEmailValidationStatus,
    setIsEmailValidating
}: {
    emailValidationStatus: validationStatusType, setEmailValidationStatus: React.Dispatch<React.SetStateAction<validationStatusType>>,
    setIsEmailValidating: React.Dispatch<React.SetStateAction<boolean>>
}) => {
    const { logout, EmailTokenSignin, EmailSignupWithOtp } = useSignIn();

    const { toastAlert } = useToastAlert();
    const { session, sessionRefresh } = useSession();

    const [ email, setEmail ] = useState<string>(session.email || "");
    const [ otp, setOtp ] = useState<string>("");
    const [ otpValidationStatus, setOtpValidationStatus ] = useState<validationStatusType>('undone');
    const [ isVisibleoOtpForm, setIsVisibleOtpForm ] = useState(false);
    const textBoxMontorTimer: React.MutableRefObject<ReturnType<typeof setTimeout> | null> = useRef(null);


    useEffect(()=>{
        if (session.memberState === 'regular'){
            if (session.email === null){
                logout();
            }else{
                setEmailValidationStatus('success');
                setOtpValidationStatus('success');
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[session])

    return <Fragment>
        <GuideTextBoxForPairedVerification
            purpose={'send'}
            text={email}
            onChange={(event: any) => {
                if (textBoxMontorTimer.current) {
                    clearTimeout(textBoxMontorTimer.current);
                }
                textBoxMontorTimer.current = setTimeout(() => {
                    logBehavior('purchase-enter-email', {});
                }, 300);

                setEmail(event.target.value);
            }}
            placeholder={''}
            isDisabled={emailValidationStatus === 'success'}
            guideTexts={{ success: '인증되었어요.' }}
            validationStatus={emailValidationStatus}
            secondStepValidationStatus={otpValidationStatus}
            onClick={async () => {
                if (!validateEmailForm(email)) {
                    logBehavior('purchase-invalid-email', {});
                    setEmailValidationStatus('fail');
                    return;
                }
                setIsEmailValidating(true);
                    
                const isEmailExist = await IsEmailLoginable(email);

                if (!isEmailExist) {
                    sessionStorage.setItem('session:email', email);
                    toastAlert("입력된 이메일로 계정을 생성합니다.", 3000, 'info');
                    setEmailValidationStatus('success');
                    setOtpValidationStatus('success');
                    setIsEmailValidating(false);
                    setIsVisibleOtpForm(false);
                    return;
                }

                setIsEmailValidating(true);

                const verifyEmailRes = await verifyEmail(email);
                logBehavior('verify-email', {});

                if (verifyEmailRes) {
                    setIsVisibleOtpForm(true);

                    logBehavior('verify-email(success)', {});
                } else {
                    toastAlert('이메일에 문제가 있어요.', 3000, 'error');
                    logBehavior('verify-email(fail)', {});
                    setEmailValidationStatus('fail');
                }
                setIsEmailValidating(false);
            }}                     
        />
        { isVisibleoOtpForm && <GuideTextBoxForStandAloneVerification
            purpose={'verification'}
            text={otp}
            onChange={(event : any) =>{ setOtp(event.target.value); }}
            placeholder={'코드가 전송됐어요. 이메일을 확인해주세요.'}
            guideTexts={{ fail: 'otp 인증에 실패했어요.' }}
            validationStatus={otpValidationStatus}
            onClick={async ()=>{
                setIsEmailValidating(true);
                const emailTokenSigninRes = await EmailTokenSignin(email, otp);
                
                if ( emailTokenSigninRes.email ) {
                    await sessionRefresh();
                    
                    logBehavior('email-token-login(success)', {});
                    setOtpValidationStatus('success');
                    setEmailValidationStatus('success');
                    setIsVisibleOtpForm(false);                                    
                } else {
                    logBehavior('email-token-login(fail)', {});
                    
                    const verifyOtpRes = await verifyOtp(email, otp);
                    
                    if ( verifyOtpRes ) {
                        const name = email.split('@')[0];
                        const emailSignupRes = await EmailSignupWithOtp(email, otp, name);

                        if ( emailSignupRes ) {
                            await sessionRefresh();

                            logBehavior('email-signup-with-otp(success)', {});
                            setOtpValidationStatus('success');
                            setEmailValidationStatus('success');
                            setIsVisibleOtpForm(false);        
                        } else {
                            toastAlert('otp 인증에 실패했어요.', 3000, 'error');
                            logBehavior('email-signup-with-otp(fail)', {});
                            setEmailValidationStatus('undone');
                            setOtpValidationStatus('fail');                                
                        }
                    } else {
                        toastAlert('otp 인증에 실패했어요.', 3000, 'error');
                        logBehavior('verify-otp(fail)', {});
                        setEmailValidationStatus('undone');
                        setOtpValidationStatus('fail');                                
                    }

                }

                setIsEmailValidating(false);
            }}
        />}
    </Fragment>
};

const CheckAndConsentOfOrder = ({consents, setConsents}: {consents: string[], setConsents: React.Dispatch<React.SetStateAction<string[]>>}) => {
    const [ modalContents, setModalContents ] = useState<string|null>(null);

    return (<div className={'CheckAndConsentOfOrder'}>
        <div className={'Title'}>
        <FormControlLabel 
            labelPlacement='end'
            control={
                <Checkbox 
                    style={{padding: 0, color: 'rgb(51,51,51)'}}
                    checked={JSON.stringify(necessaryConsents.sort()) === JSON.stringify(consents.sort())}
                    onChange={ (e) => {
                        if (e.target.checked) {
                            logBehavior('purchase-approve-agreement', {})
                            setConsents(necessaryConsents);
                        } else {
                            setConsents([]);
                        }
                    }}
                />
            }
            label={' 주문 내용 확인 및 동의'}
            />  
        </div>
        <div className={'DividingLine'} />
        {(necessaryConsents as ('개인정보 수집 및 이용 동의'|'결제대행 서비스 이용약관 동의')[]).map(consent => (
            <div key={consent}  style={{display: 'flex', justifyContent: 'space-between', margin: '10px 0', color: 'rgb(117, 117, 117)', fontSize: '0.75rem'}}>
                <div 
                    style={{
                        display: 'flex', alignItems: 'center', cursor:'pointer',
                        color: `${consents.indexOf(consent) >= 0?'rgb(0,176,240)':''}`
                    }}
                    onClick={ (e) => {
                        if ( consents.indexOf(consent) >= 0 ) {
                            setConsents(consents.filter( ele => ele !== consent ))
                        } else {
                            setConsents(consents.concat(consent))
                        }
                    }}
                >
                    <Check 
                        fontSize={'small'} 
                        style={{
                            marginRight: 15,
                        }} 
                    />
                    <div>
                        {`[필수] ${consent}`}
                    </div>
                </div>
                <div className={'DetailsOfTerms'} onClick={ () => setModalContents(consentToUrl[consent]) }>
                    {'자세히'}
                </div>
                <Dialog 
                    PaperProps={{ style: {
                        width: '100%', height: '100%'
                    }}}
                    open={Boolean(modalContents)} onClose={ () => setModalContents(null) } 
                >
                    <div style={{display: 'flex', flexDirection: 'column', width: '100%',height:'100%'}}>
                        <iframe 
                            src={modalContents||""} width='100%'  height='100%' title={modalContents||""}
                            style={{border: 0}}
                        ></iframe>
                        <div 
                            style={{
                                display: 'flex', justifyContent: 'flex-end', backgroundColor: 'transparent',
                                padding: '5px 30px'
                            }}
                        >
                            <Button
                                disableElevation variant={'contained'} color={'primary'}
                                onClick={() => setModalContents(null)}
                                size={'small'}
                            >
                                {'확인'}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </div>
        ))}
    </div>)
};

const PayButton = ({
    serviceId, titleId, productId,
    productInfoList,
    emailValidationStatus, consents, firstPaidEpisodeId,
    setIsLoading,
}: {
    serviceId: string, titleId: string, productId: typeof productKeys[number] | undefined,
    productInfoList: Partial<Record<typeof productKeys[number], Product>>,
    emailValidationStatus: validationStatusType, consents: string[], firstPaidEpisodeId: string,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
}) => {
    
    const { session } = useSession();
    const { EmailOnTemporalAccount } = useSignIn();
    const { toastAlert } = useToastAlert();
    const { refresh } = useEpisodes();

    const [ didCompletePayment, setDidCompletePayment ] = useState<boolean>(false);

    const productInfo = productId ? productInfoList[productId] : undefined;

    return (<Fragment>
        <Button 
            className={'PayButton'} fullWidth variant={'contained'} color={'primary'}
            disabled={(
                !productId || !productInfo
                || JSON.stringify(necessaryConsents.sort()) !== JSON.stringify(consents.sort())
                || emailValidationStatus !== 'success'
            )}
            onClick={ async () => {
                if ( !productId || !productInfo ) {
                    toastAlert('결제 상품이 선택되지 않았습니다.', 5000, 'error');
                    return;
                }
                if (!session.email && sessionStorage.getItem('session:email')) {
                    let email = sessionStorage.getItem('session:email');
                    await EmailOnTemporalAccount(email);
                }

                logBehavior('purchase-tosspayment-start', {})
                let popup = window.open(undefined, "팝업창", "width = 1000, height = 800, top = 50, left = 50, location = no");
                if (popup?.document?.body){
                    popup.document.body.innerHTML = "토스 페이먼츠를 로딩중입니다..";
                }

                const orderId = (session.userid || "") + Math.round(new Date().getTime()/1000);
                try {
                    const res = await createPayment({
                        method: '카드', amount: productInfo?.finalAmount, orderId, 
                        orderName: `${productInfo?.name}: ${productInfo.detail}`, 
                        orderDetail: [
                            {
                                productId,
                                quantity: 1, totalAmount: productInfo.finalAmount
                            }] });
                    
                    if (popup && res) {
                        popup.location.href = res;
                        const orderInformations  = {amount: productInfo.finalAmount, orderId};

                        let timer = setInterval(async () => {
                            if (popup?.closed) {
                                logBehavior('purchase-popup-closed', {})
                                clearInterval(timer);

                                if ( !productInfo?.name ) {
                                    toastAlert('결제 상품이 선택되지 않았습니다.', 5000, 'error');
                                    return;
                                }

                                setIsLoading(true);
                                try {
                                    const res = await approvePayment(orderInformations);
    
                                    if (res && res.orderId === orderInformations.orderId && res.amount === orderInformations.amount) {
                                        logBehavior('purchase-success-approve-tosspayment', {
                                            transaction_id: res.orderId,
                                            value: res.amount,
                                            currency: "KRW",
                                            items: [{
                                                item_id: `${serviceId}:${titleId}`,
                                                item_name: productInfo.name,
                                                quantity: 1,
                                            }],
                                            pixel: 'Purchase'
                                        })
                                        setTimeout(()=>{
                                            setIsLoading(false);
                                            setDidCompletePayment(true);
                                        },2000);
    
                                    } else {
                                        logBehavior('purchase-fail-approve-tosspayment', res)
                                        toastAlert('결제 인증에 실패했습니다.', 5000, 'error');
                                        setIsLoading(false);
                                    }

                                } catch (e) {
                                    let logObject = res;
                                    if (typeof res === 'string'){
                                        logObject = {message: res};
                                    }
                                    logBehavior('purchase-fail-approve-tosspayment', logObject)
                                    toastAlert('결제 인증에 실패했습니다.', 5000, 'error');
                                    setIsLoading(false);
                                }
                            
                            }
                        }, 10);

                    } else {
                        logBehavior('purchase-fail-tosspayment', res)
                        if(popup){
                            popup.close();
                        }   
                        toastAlert('결제에 실패했어요.', 5000, 'error');
                        // ChannelService.ChannelIO()('openChat');
                    }
                }catch(e) {
                    console.error(e);
                    toastAlert('결제에 실패했어요.', 5000, 'error');
                    popup?.close();
                }
            }}
        >
            {'결제하기'}
        </Button>
    
        <Dialog open={didCompletePayment} onClose={()=>setDidCompletePayment(false)}>
            <div style={{display: 'flex', flexDirection: 'column', minWidth: 300, fontFamily: 'inherit'}}>
                <div style={{ padding: '15px 20px'}} >
                    {"결제가 완료되었어요! 작품을 감상하러 가실래요?"}
                </div>
                <div style={{padding: '15px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Button
                        onClick={()=>{
                            refresh({serviceId, titleId});
                            setDidCompletePayment(false);
                            fn.goto(`/`)
                        }} 
                        style={{  color: 'rgb(51,51,51)', padding: '7px 20px'}}>
                        {'메인으로'}
                    </Button>
                    <Button
                        onClick={()=>{
                            refresh({serviceId, titleId});
                            setDidCompletePayment(false);
                            fn.goto(`/contents/${serviceId}/${titleId}`);
                        }} 
                        style={{ backgroundColor: 'rgb(0,176,240)', color: 'rgb(255,255,255)', padding: '7px 20px'}}>
                        {'작품 홈으로'}
                    </Button>
                </div>
            </div>
        </Dialog>
    </Fragment>
    );
}

export default ContentsPayment;